import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 font-body py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-slate-200">
        <button
          onClick={() => navigate(-1)}
          className="mb-8 flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-brand-nav transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">Privacy Policy</h1>
        
        <div className="space-y-6 text-slate-600 leading-relaxed">
          <p>
            This Privacy Policy describes how DishLCO ("we," "us," or "our") collects, uses, and protects your personal information. This policy applies to SuperAdmins, Local Cable Operators (LCOs/Admins), Dealers, Staff, and End Customers using our white-labeled DTH recharge and management platform.
          </p>

          <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">1. Information We Collect</h2>
          <p>
            Depending on your role, we collect different types of information:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Operators & Dealers:</strong> Business names, contact details (email, mobile), encrypted API keys, and payment gateway credentials (secured via AES-256).</li>
            <li><strong>Customers:</strong> Name, contact information, DTH subscriber IDs, recharge history, and transaction records needed to facilitate DTH services.</li>
            <li><strong>Automatically Collected Data:</strong> IP addresses, browser types, and usage data to monitor platform security and performance.</li>
          </ul>

          <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">2. How We Use Your Information</h2>
          <p>
            We use the collected information for the following core platform operations:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Processing DTH recharges and managing subscriber plans through integrated operator gateways (e.g., Dish TV).</li>
            <li>Processing payments securely through configured gateways (Razorpay, Paytm, Cashfree, PhonePe).</li>
            <li>Sending automated lifecycle (LCM) alerts (30, 15, and 7-day expiry warnings) via SMS, Email, and WhatsApp.</li>
            <li>Maintaining immutable ledgers and commission reports for dealers and operators.</li>
            <li>Provisioning and operating white-label tenant subdomains and custom domains.</li>
          </ul>

          <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">3. Data Security & Encryption</h2>
          <p>
            Security is critical to our infrastructure. All sensitive credentials, including operator recharge API keys and payment gateway secrets, are encrypted at rest using AES-256-CBC. Each set of credentials is strictly scoped to the respective admin tenant to ensure complete data isolation across our multi-tenant architecture.
          </p>

          <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">4. Sharing Your Information</h2>
          <p>
            We do not sell your personal information. We only share data with necessary third parties, such as:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Payment Processors:</strong> To authorize and complete recharge transactions. We do not store your full credit card details.</li>
            <li><strong>DTH Operators:</strong> Subscriber IDs and recharge amounts are transmitted to DTH service providers to activate your plans.</li>
            <li><strong>Communication Providers:</strong> To deliver OTPs, payment receipts, and automated expiry alerts.</li>
          </ul>

          <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">5. Data Retention</h2>
          <p>
            We retain your data, including transaction ledgers, for as long as your account is active or as needed to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements.
          </p>

          <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">6. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy, data management, or security practices, please contact us through the portal's support channels or email our compliance team.
          </p>
        </div>
      </div>
    </div>
  );
}
